import React, { useEffect, useState } from "react";
import { Button, Loader, Modal, Table, Timeline } from "@mantine/core";
import { Check, Checkbox, Eye, Fence, PlayerPause, PlayerPlay } from "tabler-icons-react";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { Trip } from "@captiva/shared-types";

interface TemplatePhoto {
  path: string;
}

interface TemplateData {
  deviceCaptured: any;
  notes?: string;
  odoOrHourReading?: string | number;
  odoOrHourReadingPhoto?: TemplatePhoto[];
  fleetOperatorSignedImage?: TemplatePhoto[];
  documentingPhotos?: TemplatePhoto[];
}

interface TemplateItem {
  template: string;
  data: TemplateData;
}

async function fetchAndPushTemplateData(
  templateIdArray: string[] | undefined,
  templateName: string,
  templateArraySetter: React.Dispatch<React.SetStateAction<TemplateItem[]>>
) {
  if (!templateIdArray) return;
  const numberTemplates = templateIdArray.length;
  for (let index = 0; index < numberTemplates; index++) {
    const id = templateIdArray[index];
    const templateDoc = await getDoc(doc(db, templateName, id));
    if (templateDoc.exists()) {
      const docData = templateDoc.data() as TemplateData;
      templateArraySetter((t) => [
        ...t,
        { template: templateName, data: docData },
      ]);
    }
  }
}

function toTimestampToDateString(value: any): string {
  if (!value) return "Unknown";
  if (value.seconds !== undefined || value._seconds !== undefined) {
    const seconds = value.seconds !== undefined ? value.seconds : value._seconds;
    return (
      new Date(seconds * 1000).toLocaleDateString("en-GB") +
      " " +
      new Date(seconds * 1000).toLocaleTimeString("en-GB")
    );
  } else {
    const date = new Date(value);
    if (isNaN(date.getTime())) return "Unknown";
    return date.toLocaleDateString("en-GB") || "Unknown";
  }
}

const captureLabels: Record<string, string> = {
  capturedTripPreInspection: "Pre trip inspection",
  capturedTripArrive: "Arrive",
  capturedTripDepart: "Depart",
  capturedTripCheckPoint: "Checkpoint",
  capturedTripDelay: "Delay",
  capturedTripComplete: "Complete",
};

interface CaptureDetailModalProps {
  capture: TemplateItem | null;
  onClose: () => void;
}

function CaptureDetailModal({ capture, onClose }: CaptureDetailModalProps) {
  if (!capture) return null;
  const { template, data } = capture;
  const title = captureLabels[template] || template;
  return (
    <Modal opened={!!capture} onClose={onClose} title={title} size="lg">
      <Table style={{ border: "1px solid lightgray", marginBottom: "1em" }}>
        <tbody>
          <tr>
            <td>Timestamp</td>
            <td>{toTimestampToDateString(data.deviceCaptured)}</td>
          </tr>
          <tr>
            <td>Notes</td>
            <td>{data.notes ?? "(empty)"}</td>
          </tr>
          {template === "capturedTripPreInspection" && (
            <tr>
              <td>ODO / Hour reading</td>
              <td>{data.odoOrHourReading ?? "(empty)"}</td>
            </tr>
          )}
        </tbody>
      </Table>

      {template === "capturedTripPreInspection" && data?.odoOrHourReadingPhoto && data.odoOrHourReadingPhoto.length > 0 && (
        <div style={{ marginBottom: "1em" }}>
          <strong>ODO photo</strong>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "4px" }}>
            {data.odoOrHourReadingPhoto.map((photo, i) => (
              <a key={photo.path + i} href={photo.path} target="_blank" rel="noopener noreferrer">
                <img src={photo.path} alt="ODO" style={{ maxWidth: "300px", maxHeight: "300px", objectFit: "contain" }} />
              </a>
            ))}
          </div>
        </div>
      )}

      {template === "capturedTripPreInspection" && data?.fleetOperatorSignedImage && data.fleetOperatorSignedImage.length > 0 && (
        <div style={{ marginBottom: "1em" }}>
          <strong>Signature</strong>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "4px" }}>
            {data.fleetOperatorSignedImage.map((photo, i) => (
              <a key={photo.path + i} href={photo.path} target="_blank" rel="noopener noreferrer">
                <img src={photo.path} alt="Signature" style={{ maxWidth: "300px", maxHeight: "300px", objectFit: "contain" }} />
              </a>
            ))}
          </div>
        </div>
      )}

      {data?.documentingPhotos && data.documentingPhotos.length > 0 && (
        <div style={{ marginBottom: "1em" }}>
          <strong>Documenting photos</strong>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "4px" }}>
            {data.documentingPhotos.map((photo, i) => (
              <a key={photo.path + i} href={photo.path} target="_blank" rel="noopener noreferrer">
                <img src={photo.path} alt="Docs" style={{ maxWidth: "300px", maxHeight: "300px", objectFit: "contain" }} />
              </a>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

interface TripTimelineProps {
  trip: Trip;
}

function TripTimeline({ trip }: TripTimelineProps) {
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCapture, setSelectedCapture] = useState<TemplateItem | null>(null);

  useEffect(() => {
    if (!trip.id) return;
    if (isLoading) return;
    const fetchData = async () => {
      setIsLoading(true);
      await fetchAndPushTemplateData(
        trip.capturedTripPreInspection,
        "capturedTripPreInspection",
        setTemplates
      );
      await fetchAndPushTemplateData(
        trip.capturedTripDepart,
        "capturedTripDepart",
        setTemplates
      );
      await fetchAndPushTemplateData(
        trip.capturedTripCheckPoint,
        "capturedTripCheckPoint",
        setTemplates
      );
      await fetchAndPushTemplateData(
        trip.capturedTripComplete,
        "capturedTripComplete",
        setTemplates
      );
      await fetchAndPushTemplateData(
        trip.capturedTripDelay,
        "capturedTripDelay",
        setTemplates
      );
      await fetchAndPushTemplateData(
        trip.capturedTripArrive,
        "capturedTripArrive",
        setTemplates
      );
      setIsLoading(false);
    };

    fetchData();

    return () => {
      setTemplates([]);
    };
  }, [trip]);

  return (
    <div
      style={{
        padding: "1em",
        width: "100%",
        height: "100%",
        overflow: "scroll",
      }}
    >
      {isLoading ? (
        <Loader style={{ margin: "auto" }} />
      ) : (
        <>
          <CaptureDetailModal capture={selectedCapture} onClose={() => setSelectedCapture(null)} />
          <h3>Details</h3>
          <Table style={{ border: "1px solid lightgray" }}>
            <tbody>
              <tr>
                <td>Description</td>
                <td>{trip.description}</td>
              </tr>
              <tr>
                <td>Pickup address</td>
                <td>
                  {trip.pickupAddress ? (
                    <a
                      href={
                        trip.pickupCoordinates
                          ? `https://www.google.com/maps/search/?api=1&query=${trip.pickupCoordinates.latitude},${trip.pickupCoordinates.longitude}`
                          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trip.pickupAddress)}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#228be6", textDecoration: "underline" }}
                    >
                      {trip.pickupAddress}
                    </a>
                  ) : (
                    "(empty)"
                  )}
                </td>
              </tr>
              <tr>
                <td>Planned pickup date</td>
                <td>{toTimestampToDateString(trip.pickupDate)}</td>
              </tr>
              <tr>
                <td>Delivery address</td>
                <td>
                  {trip.deliveryAddress ? (
                    <a
                      href={
                        trip.deliveryCoordinates
                          ? `https://www.google.com/maps/search/?api=1&query=${trip.deliveryCoordinates.latitude},${trip.deliveryCoordinates.longitude}`
                          : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trip.deliveryAddress)}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#228be6", textDecoration: "underline" }}
                    >
                      {trip.deliveryAddress}
                    </a>
                  ) : (
                    "(empty)"
                  )}
                </td>
              </tr>
              <tr>
                <td>Planned delivery date</td>
                <td>{toTimestampToDateString(trip.deliveryDate)}</td>
              </tr>
              <tr>
                <td>Notes</td>
                <td>{trip.notes || "(empty)"}</td>
              </tr>
              <tr>
                <td>Status</td>
                <td>{trip.status}</td>
              </tr>
              <tr>
                <td>Tracking active</td>
                <td>{trip.isTrackingActive ? "TRUE" : "FALSE"}</td>
              </tr>
              <tr>
                <td>External reference</td>
                <td>{trip.externalReference || "(empty)"}</td>
              </tr>
              <tr>
                <td>Vehicle</td>
                <td>{trip.fleetDisplayName || "(unassigned)"}</td>
              </tr>
              <tr>
                <td>Vehicle registration</td>
                <td>{trip.fleetRegistration || "(unassigned)"}</td>
              </tr>
              <tr>
                <td>Vehicle group</td>
                <td>{trip.fleetGroupName || "(unassigned)"}</td>
              </tr>
              <tr>
                <td>Driver</td>
                <td>{trip.fleetOperatorNameAndSurname || "(unassigned)"}</td>
              </tr>
              <tr>
                <td>Driver employee code</td>
                <td>{trip.fleetOperatorEmployeeCode || "(unassigned)"}</td>
              </tr>
              <tr>
                <td>Driver identification</td>
                <td>{trip.fleetOperatorIdentification || "(unassigned)"}</td>
              </tr>
              <tr>
                <td>Requires pre-inspection</td>
                <td>{trip.requiresPreInspection ? "TRUE" : "FALSE"}</td>
              </tr>
            </tbody>
          </Table>
          {templates.length > 0 && <h3>Timeline</h3>}
          <Timeline style={{ width: "100%" }}>
            {templates
              .sort((a, b) => {
                const timeA = typeof a.data.deviceCaptured === "number" ? a.data.deviceCaptured : new Date(a.data.deviceCaptured).getTime();
                const timeB = typeof b.data.deviceCaptured === "number" ? b.data.deviceCaptured : new Date(b.data.deviceCaptured).getTime();
                return timeA - timeB;
              })
              .map((t, i) => {
                switch (t.template) {
                  case "capturedTripPreInspection":
                    return (
                      <Timeline.Item
                        key={i}
                        title="Pre trip inspection"
                        bullet={<Eye size={24} color="green" />}
                      >
                        <div>
                          <div>{toTimestampToDateString(t.data.deviceCaptured)}</div>
                          <div>ODO: {t.data.odoOrHourReading}</div>
                          <div>Notes: {t.data.notes ?? "(empty)"}</div>
                          <Button
                            size="sm"
                            variant="outline"
                            color="cap-navy"
                            mt={4}
                            style={{ minHeight: 36 }}
                            onClick={() => setSelectedCapture(t)}
                          >
                            {(t.data?.documentingPhotos && t.data.documentingPhotos.length > 0) ||
                             (t.data?.odoOrHourReadingPhoto && t.data.odoOrHourReadingPhoto.length > 0) ||
                             (t.data?.fleetOperatorSignedImage && t.data.fleetOperatorSignedImage.length > 0)
                              ? "View details & photos"
                              : "View details"}
                          </Button>
                        </div>
                      </Timeline.Item>
                    );
                  case "capturedTripArrive":
                    return (
                      <Timeline.Item
                        key={i}
                        title="Arrive"
                        bullet={<Fence size={24} color="green" />}
                      >
                        <div>
                          <div>{toTimestampToDateString(t.data.deviceCaptured)}</div>
                          <div>Notes: {t.data.notes ?? "(empty)"}</div>
                          <Button
                            size="sm"
                            variant="outline"
                            color="cap-navy"
                            mt={4}
                            style={{ minHeight: 36 }}
                            onClick={() => setSelectedCapture(t)}
                          >
                            {t.data?.documentingPhotos && t.data.documentingPhotos.length > 0 ? "View details & photos" : "View details"}
                          </Button>
                        </div>
                      </Timeline.Item>
                    );
                  case "capturedTripDepart":
                    return (
                      <Timeline.Item
                        key={i}
                        title="Depart"
                        bullet={<PlayerPlay size={24} color="green" />}
                      >
                        <div>
                          <div>{toTimestampToDateString(t.data.deviceCaptured)}</div>
                          <div>Notes: {t.data.notes ?? "(empty)"}</div>
                          <Button
                            size="sm"
                            variant="outline"
                            color="cap-navy"
                            mt={4}
                            style={{ minHeight: 36 }}
                            onClick={() => setSelectedCapture(t)}
                          >
                            {t.data?.documentingPhotos && t.data.documentingPhotos.length > 0 ? "View details & photos" : "View details"}
                          </Button>
                        </div>
                      </Timeline.Item>
                    );
                  case "capturedTripCheckPoint":
                    return (
                      <Timeline.Item
                        key={i}
                        title="Checkpoint"
                        bullet={<Check size={24} color="green" />}
                      >
                        <div>
                          <div>{toTimestampToDateString(t.data.deviceCaptured)}</div>
                          <div>Notes: {t.data.notes ?? "(empty)"}</div>
                          <Button
                            size="sm"
                            variant="outline"
                            color="cap-navy"
                            mt={4}
                            style={{ minHeight: 36 }}
                            onClick={() => setSelectedCapture(t)}
                          >
                            {t.data?.documentingPhotos && t.data.documentingPhotos.length > 0 ? "View details & photos" : "View details"}
                          </Button>
                        </div>
                      </Timeline.Item>
                    );
                  case "capturedTripDelay":
                    return (
                      <Timeline.Item
                        key={i}
                        title="Delay"
                        bullet={<PlayerPause size={24} color="green" />}
                      >
                        <div>
                          <div>{toTimestampToDateString(t.data.deviceCaptured)}</div>
                          <div>Notes: {t.data.notes ?? "(empty)"}</div>
                          <Button
                            size="sm"
                            variant="outline"
                            color="cap-navy"
                            mt={4}
                            style={{ minHeight: 36 }}
                            onClick={() => setSelectedCapture(t)}
                          >
                            {t.data?.documentingPhotos && t.data.documentingPhotos.length > 0 ? "View details & photos" : "View details"}
                          </Button>
                        </div>
                      </Timeline.Item>
                    );
                  case "capturedTripComplete":
                    return (
                      <Timeline.Item
                        key={i}
                        title="Complete"
                        bullet={<Checkbox size={24} color="green" />}
                      >
                        <div>
                          <div>{toTimestampToDateString(t.data.deviceCaptured)}</div>
                          <div>Notes: {t.data.notes ?? "(empty)"}</div>
                          <Button
                            size="sm"
                            variant="outline"
                            color="cap-navy"
                            mt={4}
                            style={{ minHeight: 36 }}
                            onClick={() => setSelectedCapture(t)}
                          >
                            {t.data?.documentingPhotos && t.data.documentingPhotos.length > 0 ? "View details & photos" : "View details"}
                          </Button>
                        </div>
                      </Timeline.Item>
                    );
                  default:
                    return <div key={i}>Unknown template type</div>;
                }
              })}
          </Timeline>
        </>
      )}
    </div>
  );
}

export { TripTimeline };
export default TripTimeline;